import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApi, type ReportDamageRequest, type ReportMissingItemRequest } from '@/shared/api';
import { cleaningFailure } from '../cleaningFailure';

/**
 * Mutationen der Meldungen-Reiter (Schäden/Fehlt): Report anlegen, quittieren, Foto anhängen/
 * entfernen. Report-Fotos hängen an `damageReportId` und landen (Scope-Grenze Block B: kein neuer
 * Backend-Kind) als generische Issue-Evidenz im Akte-Ordner `08_Fotos/Schaeden`. Fehler kommen als
 * deutscher Satz; ohne Antwort sagt er, dass offen ist, ob es ankam – der Stand lädt in jedem Fall neu.
 */
export function useReportMutations(taskId: string) {
  const api = useApi();
  const qc = useQueryClient();
  const settle = {
    networkMode: 'always' as const,
    onSettled: () => void qc.invalidateQueries({ queryKey: ['cleaning'] }),
  };
  const fail = (fallback: string) => async (err: unknown) =>
    toast.error((await cleaningFailure(err, fallback)).text);

  const reportDamage = useMutation({
    mutationFn: (body: ReportDamageRequest) => api.cleaning.reportDamage(taskId, body),
    onSuccess: () => toast.success('Schaden gemeldet – die Mail dazu steht in der Warteschlange'),
    onError: fail('Schaden nicht gemeldet.'),
    ...settle,
  });

  const reportMissing = useMutation({
    mutationFn: (body: ReportMissingItemRequest) => api.cleaning.reportMissingItem(taskId, body),
    onSuccess: () => toast.success('Fehlteil gemeldet – die Mail dazu steht in der Warteschlange'),
    onError: fail('Fehlteil nicht gemeldet.'),
    ...settle,
  });

  const acknowledge = useMutation({
    mutationFn: (reportId: string) => api.cleaning.acknowledgeDamage(taskId, reportId),
    onSuccess: () => toast.success('Meldung bestätigt'),
    onError: fail('Meldung nicht bestätigt.'),
    ...settle,
  });

  const attachPhoto = useMutation({
    mutationFn: (vars: { reportId: string; file: File }) =>
      api.cleaning.uploadPhoto(taskId, vars.file, 'Damage', null, vars.reportId),
    onSuccess: () => toast.success('Foto gespeichert'),
    onError: fail('Foto nicht gespeichert.'),
    ...settle,
  });

  const removePhoto = useMutation({
    mutationFn: (photoId: string) => api.cleaning.deletePhoto(taskId, photoId),
    onSuccess: () => toast.success('Foto entfernt'),
    onError: fail('Foto nicht entfernt.'),
    ...settle,
  });

  return { reportDamage, reportMissing, acknowledge, attachPhoto, removePhoto };
}
